import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Package, Clock, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ORDER_STATUSES = [
  'pending',
  'confirmed',
  'preparing',
  'ready',
  'out_for_delivery',
  'delivered',
  'cancelled',
];

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const STATUS_BADGE_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-blue-50 text-blue-700 border-blue-200',
  preparing: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  ready: 'bg-purple-50 text-purple-700 border-purple-200',
  out_for_delivery: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
};

const STATUS_ACCENT = {
  pending: 'before:bg-amber-400',
  confirmed: 'before:bg-blue-400',
  preparing: 'before:bg-indigo-400',
  ready: 'before:bg-purple-400',
  out_for_delivery: 'before:bg-cyan-400',
  delivered: 'before:bg-emerald-400',
  cancelled: 'before:bg-red-400',
};

const formatTimeAgo = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
      STATUS_BADGE_STYLES[status] || 'bg-gray-50 text-gray-600 border-gray-200'
    }`}
    data-testid={`order-status-${status}`}
  >
    {STATUS_LABELS[status] || status}
  </span>
);

const VendorOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/vendor/orders`, {
          withCredentials: true,
        });
        setOrders(res.data?.orders || []);
        setStatusCounts(res.data?.status_counts || {});
      } catch (err) {
        console.error('Failed to fetch orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const filtered = useMemo(() => {
    if (activeTab === 'all') return orders;
    return orders.filter((o) => o.status === activeTab);
  }, [orders, activeTab]);

  return (
    <div data-testid="vendor-orders-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">Orders</h1>
        <p className="font-body text-text-secondary">
          Track and manage customer orders
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="order-status-tabs">
        <TabButton
          label="All"
          count={orders.length}
          active={activeTab === 'all'}
          onClick={() => setActiveTab('all')}
          testId="order-tab-all"
        />
        {ORDER_STATUSES.map((s) => (
          <TabButton
            key={s}
            label={STATUS_LABELS[s]}
            count={statusCounts[s] || 0}
            active={activeTab === s}
            onClick={() => setActiveTab(s)}
            testId={`order-tab-${s}`}
          />
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-surface-border p-12 flex flex-col items-center justify-center text-text-muted">
          <ShoppingCart size={48} className="mb-4 opacity-40" />
          <p className="font-body text-lg">
            {activeTab === 'all'
              ? 'No orders yet'
              : `No ${STATUS_LABELS[activeTab]?.toLowerCase()} orders`}
          </p>
          <p className="font-body text-sm mt-1">
            New orders from your apartment community will show up here.
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          data-testid="orders-grid"
        >
          {filtered.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              onOpen={() => navigate(`/vendor/orders/${o.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TabButton = ({ label, count, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-body font-medium border transition-colors ${
      active
        ? 'bg-brand-primary text-white border-brand-primary'
        : 'bg-white text-text-secondary border-surface-border hover:text-text-primary hover:border-text-muted'
    }`}
  >
    <span>{label}</span>
    <span
      className={`text-xs px-1.5 py-0.5 rounded-full ${
        active ? 'bg-white/20 text-white' : 'bg-background-secondary text-text-muted'
      }`}
    >
      {count}
    </span>
  </button>
);

const OrderCard = ({ order, onOpen }) => {
  const items = order.items || [];
  const itemPreview = items
    .slice(0, 3)
    .map((i) => `${i.quantity}× ${i.name}`)
    .join(' • ');
  const more = items.length - 3;
  const accent = STATUS_ACCENT[order.status] || 'before:bg-gray-300';

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`order-card-${order.id}`}
      className={`relative text-left bg-white rounded-2xl border border-surface-border p-4 sm:p-5 hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col gap-3 group overflow-hidden before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 ${accent}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-heading font-bold text-base text-text-primary truncate">
            {order.order_number}
          </p>
          <p className="text-xs text-text-muted font-body inline-flex items-center gap-1 mt-0.5">
            <Clock size={11} />
            {formatTimeAgo(order.created_at)}
          </p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {/* Customer */}
      <div className="space-y-0.5">
        <p className="font-body text-sm font-medium text-text-primary truncate">
          {order.customer_name || 'Customer'}
        </p>
        {order.customer_phone && (
          <p className="font-body text-xs text-text-muted inline-flex items-center gap-1.5 truncate">
            <Phone size={11} />
            {order.customer_phone}
          </p>
        )}
      </div>

      {/* Items preview */}
      <div className="bg-background-secondary rounded-xl p-3 flex-1">
        <p className="font-body text-xs text-text-muted line-clamp-2">
          <Package size={12} className="inline mr-1 -mt-0.5" />
          {itemPreview || 'No items'}
          {more > 0 ? ` +${more} more` : ''}
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-baseline justify-between pt-1 border-t border-surface-border">
        <span className="text-xs text-text-muted font-body">
          {order.item_count} item{order.item_count === 1 ? '' : 's'}
        </span>
        <span className="font-heading font-bold text-lg text-text-primary">
          ₹{Number(order.total_amount || 0).toFixed(0)}
        </span>
      </div>
    </button>
  );
};

export default VendorOrders;
